import { BehaviorSubject, distinctUntilChanged, map, Observable, tap } from "rxjs";
import zustand from "zustand";
import shallow from "zustand/shallow";
import { createAxios, isError4xx, isError401, isErrorCanceled, useCommStore } from "./axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { Dictionary, groupBy, isEqual, values } from "lodash";
import { AxiosResponse } from "axios";
import { delay, KeysOfTypeSet, useRxJsSub } from "./utils";

export interface EntityStore<T> {
    serverData: T;
    isInitialized: boolean;
    isFetching: boolean;
    isFetched: boolean;
    notFound: boolean;
    failedToFetch: boolean;
    isSaving: boolean;
    isClientDataSame: boolean;
    clientData: T;
    entityId: string | number | null | undefined,
    serverData$: Observable<{ data: T; isFetching: boolean; }>;
    clientData$: Observable<{ data: T; isFetching: boolean; }>;
    isSaveDisabled: boolean;
    propValidationMessages: Partial<Record<keyof T, ValidationMessage[]>>;
    propValidationMessages$: Observable<Partial<Record<keyof T, ValidationMessage[]>>>;
    propValidationErrors: Partial<Record<keyof T, string[]>>;
    propValidationErrors$: Observable<Partial<Record<keyof T, string[]>>>;
    isValidationEnabled: boolean;
    isValidationEnabled$: Observable<boolean>;
    propValidators: PropValidator<T>[];
    saveProps?: (keyof T)[];
    saveAttemptLimit?: number;
    fetchServerData(updateClientData?: boolean, customFetch?: (path: string, signal: AbortSignal, qs: any) => Promise<AxiosResponse<T>>): void;
    setServerData(data: T): void;
    setClientData(data: T): void;
    setClientDataFunc(setData: (data: T) => T): void;
    setClientDataProp<U extends keyof T>(prop: U, value: T[U]): void;
    setServerDataProp<U extends keyof T>(prop: U, value: T[U]): void;
    save(): void;
    saveEx(ignoreSaveDisabled?: boolean): Promise<boolean | null>;
    init(): void;
    setPropValidators(propValidators: (PropValidator<T> | null | false | undefined)[]): void;
    reset(): void;
    enableValidation(): void;
    disableValidation(): void;
    setSaveProps(saveProps: (keyof T)[] | undefined): void;
    addDynamicPropValidator(validator: PropValidator<T>): number;
    removeDynamicPropValidator(id: number): void;
    onDataChanged(serverDataChanged: boolean): void;
    handleSaveError?(error: any): Promise<void>;
}

interface EntityStoreInternal<T> extends EntityStore<T> {
    abortController: AbortController | null;
    saveAbortController: AbortController | null;
    lastDynamicPropValidatorId: number;
    dynamicPropValidators: Dictionary<PropValidator<T>>;
}

export function getDataForSave<T extends {}>(data: T, saveProps: (keyof T)[] | undefined) {
    if (saveProps == null) {
        return data;
    }
    const dataForSave = {} as T;
    for (const prop of saveProps) {
        dataForSave[prop] = data[prop];
    }
    return dataForSave;
}

export type PropValidator<T> = (clientData: T) => { prop: keyof T; messages: ValidationMessage[]; } | null;
export type PropValidatorEx<T> = PropValidator<T> | null | false;
export type ValidationMessageType = "error" | "recommended";
export type ValidationMessage = string | { message: string; type: ValidationMessageType; }

export interface UseEntityStore<T> {
    (): EntityStore<T>;
    <U = Omit<EntityStore<T>, "init">>(selector?: (store: EntityStore<T>) => U, compare?: (a: U, b: U) => boolean): U;
    getState(): EntityStore<T>;
}

export function createEntityStore<T extends {}>(config: {
    path?: string;
    qs?: { [name: string]: string | number | null | undefined; },
    data: T;
    saveProps?: (keyof T)[];
    id?: string | number | null;
    getNewId?(entity: T): string | number;
    onServerDataChanged?(entity: T): void;
    onCreated?(entity: T): void;
    onSave?(entity: T): void;
    onSaved?(entity: T): void;
    checkDataBeforeSave?(entity: T, entityId: string | number | null | undefined): Promise<boolean>;
    propValidators?: (PropValidator<T> | null | false)[];
    fetchOnInit?: false;
    isValidationEnabled?: true;
    customFetch?(path: string, signal: AbortSignal, qs: any): Promise<AxiosResponse<T>>;
    transformFetchedData?(data: T): T;
    onFetched?(): void;
    getDataForSave?(data: T): Partial<T>;
    saveAttemptLimit?: number;
    handleSaveError?(error: any): Promise<void>;
}): UseEntityStore<T> {
    const fetchOnInit = config.fetchOnInit !== false && config.path != null && config.id !== null;
    const serverData$ = new BehaviorSubject<{ data: T; isFetching: boolean; }>({
        data: config.data,
        isFetching: fetchOnInit,
    });
    const clientData$ = new BehaviorSubject<{ data: T; isFetching: boolean; }>({
        data: config.data,
        isFetching: fetchOnInit,
    });
    const isValidationEnabled = config.isValidationEnabled == null ? false : config.isValidationEnabled;
    const isValidationEnabled$ = new BehaviorSubject(isValidationEnabled);
    const propValidationErrors$ = new BehaviorSubject<Partial<Record<keyof T, string[]>>>({});
    const propValidationMessages$ = new BehaviorSubject<Partial<Record<keyof T, ValidationMessage[]>>>({});
    const useStore = zustand<EntityStoreInternal<T>>((set, get) => ({
        serverData: config.data,
        clientData: config.data,
        isFetching: fetchOnInit,
        isInitialized: false,
        notFound: false,
        failedToFetch: false,
        isFetched: !fetchOnInit,
        isSaving: false,
        entityId: config.id,
        serverData$: serverData$.pipe(tap({
            subscribe() {
                get().init();
            },
            unsubscribe() {
            },
        })),
        clientData$: clientData$.pipe(tap({
            subscribe() {
                get().init();
            },
            unsubscribe() {
            },
        })),
        isClientDataSame: true,
        isSaveDisabled: config.id != null || !fetchOnInit,
        propValidationErrors: {},
        propValidationMessages: {},
        propValidationErrors$,
        propValidationMessages$,
        isValidationEnabled,
        isValidationEnabled$,
        propValidators: config.propValidators?.filter(p => p) as PropValidator<T>[] || [],
        abortController: null,
        saveAbortController: null,
        saveProps: config.saveProps,
        dynamicPropValidators: {},
        lastDynamicPropValidatorId: 0,
        saveAttemptLimit: config.saveAttemptLimit,
        handleSaveError: config.handleSaveError,
        fetchServerData: (updateClientData = false, customFetch?: (path: string, signal: AbortSignal, qs: any) => Promise<AxiosResponse<T>>) => {
            const pathBase = config.path;
            if (pathBase == null) {
                return;
            }
            get().abortController?.abort();
            get().saveAbortController?.abort();
            const abortController = new AbortController();
            set({ isFetching: true, abortController });
            get().onDataChanged(true);
            (async () => {
                try {
                    let path = pathBase;
                    const id = get().entityId;
                    if (id != null) {
                        path += "/" + id;
                    }
                    while (true) {
                        useCommStore.getState().requestStarted();
                        try {
                            const { data } = await (customFetch ? customFetch(path, abortController.signal, config.qs) :
                                config.customFetch ? config.customFetch(path, abortController.signal, config.qs) :
                                    createAxios().get<T>(path, { signal: abortController.signal, params: config.qs }));
                            const finalData = config.transformFetchedData ? config.transformFetchedData(data) : data;
                            const clientData = get().isFetched && !updateClientData ? get().clientData : finalData;
                            set({ isFetching: false, abortController: null, isFetched: true, serverData: finalData, clientData, failedToFetch: false });
                            get().onDataChanged(true);
                            config.onFetched?.();
                            break;
                        } catch (error) {
                            if (isErrorCanceled(error) || isError401(error)) {
                                throw error;
                            }

                            await delay(1000);
                        } finally {
                            useCommStore.getState().requestFinished(null);
                        }
                    }
                } catch (err: any) {
                    set({ abortController: null, notFound: err?.response?.status === 404, failedToFetch: true, isFetching: false });
                    get().onDataChanged(true);
                    config.onFetched?.();
                }
            })();
        },
        setServerData: (serverData) => {
            set({ serverData });
            get().onDataChanged(true);
        },
        setClientData: (clientData) => {
            set({ clientData });
            get().onDataChanged(false);
        },
        setClientDataFunc(setData) {
            set(p => ({ clientData: setData(p.clientData) }));
        },
        setClientDataProp<U extends keyof T>(prop: U, value: T[U]) {
            const newObj: T = { ...get().clientData };
            newObj[prop] = value;
            get().setClientData(newObj);
        },
        setServerDataProp<U extends keyof T>(prop: U, value: T[U]) {
            const newObj: T = { ...get().serverData };
            newObj[prop] = value;
            get().setServerData(newObj);
        },
        save: async () => {
            get().saveEx();
        },
        saveEx: async (ignoreSaveDisabled: boolean = false) => {
            const pathBase = config.path;
            if (!get().isValidationEnabled) {
                set({ isValidationEnabled: true });
                isValidationEnabled$.next(true);
                get().onDataChanged(false);
            }
            if (get().isSaveDisabled && !ignoreSaveDisabled) {
                return false;
            }
            config.onSave?.(get().clientData);
            if (pathBase == null) {
                return false;
            }
            get().abortController?.abort();
            get().saveAbortController?.abort();
            const saveAbortController = new AbortController();
            set({ isSaving: true, saveAbortController, isSaveDisabled: true });
            const isCurrentSave = () => get().saveAbortController === saveAbortController;
            const releaseSave = () => {
                if (isCurrentSave()) {
                    set({ isSaving: false, saveAbortController: null });
                    get().onDataChanged(true);
                }
            };
            const saveAttemptLimit = get().saveAttemptLimit ?? 1;
            let finished: boolean | null = null;
            for (let i = 0; i < saveAttemptLimit && !saveAbortController.signal.aborted && finished == null; i++) {
                if (i > 0) {
                    await delay(1000);
                    if (saveAbortController.signal.aborted) {
                        break;
                    }
                }
                try {
                    let entityId = get().entityId;
                    let clientData = get().clientData;
                    let serverData = clientData;
                    const isCreate = entityId === null;
                    if ((!config.checkDataBeforeSave || await config.checkDataBeforeSave(clientData, entityId)) && (ignoreSaveDisabled || Object.keys(get().propValidationErrors).length === 0)) {
                        if (saveAbortController.signal.aborted) {
                            break;
                        }
                        clientData = get().clientData;
                        const clientDataForSave = config.getDataForSave?.(clientData) ?? getDataForSave(clientData, get().saveProps);
                        serverData = clientData;
                        let path = pathBase + (entityId == null ? "" : "/" + entityId);
                        if (isCreate) {
                            const { data } = await createAxios(undefined, false).post<T>(path, clientDataForSave, { signal: saveAbortController.signal, params: config.qs });
                            serverData = { ...serverData, ...data };
                            if (config.getNewId) {
                                entityId = config.getNewId(serverData);
                            }
                        } else {
                            const { data } = await createAxios(undefined, false).put<T>(path, clientDataForSave, { signal: saveAbortController.signal, params: config.qs });
                            serverData = { ...serverData, ...data };
                        }

                        if (!isCurrentSave()) {
                            finished = true;
                            break;
                        }

                        set({ entityId, isSaving: false, saveAbortController: null, serverData, clientData: { ...serverData, ...clientData } });

                        if (isCreate && config.onCreated) {
                            config.onCreated(serverData);
                        }
                        if (config.onSaved) {
                            config.onSaved(serverData);
                        }

                        get().onDataChanged(true);
                        finished = true;
                    } else {
                        releaseSave();
                        get().onDataChanged(false);
                        finished = false;
                    }
                } catch (err: any) {
                    if (isErrorCanceled(err) || saveAbortController.signal.aborted) {
                        releaseSave();
                        break;
                    }
                    if (i < saveAttemptLimit - 1 && !isError4xx(err)) {
                        continue;
                    }

                    if (!isCurrentSave()) {
                        break;
                    }

                    set({ isSaving: false, saveAbortController: null });
                    get().onDataChanged(true);
                    finished = false;
                    const handleSaveError = get().handleSaveError;
                    if (handleSaveError) {
                        await handleSaveError(err);
                    } else {
                        await useCommStore.getState().handleError(err);
                    }
                }
            }
            releaseSave();
            return finished;
        },
        init: () => {
            if (get().isInitialized && !get().failedToFetch) {
                return;
            }
            if (fetchOnInit) {
                get().fetchServerData(true);
            } else {
                get().onDataChanged(true);
            }
            set({ isInitialized: true });
            return;
        },
        onDataChanged: (serverDataChanged: boolean) => {
            const clientData = get().clientData;
            const validators = get().isValidationEnabled ? get().propValidators.concat(values(get().dynamicPropValidators)) : [];
            const propValidationMessages = Object.fromEntries(
                Object.entries(groupBy(validators.map(p => p(clientData)).filter(p => p != null), p => p!.prop))
                    .map(([key, value]) => [key, value.map(p => p!.messages).flat(1)])
                    .filter(p => p[1].length > 0)) as Dictionary<ValidationMessage[]>;
            const propValidationErrors = Object.fromEntries(Object.entries(propValidationMessages)
                .map<[string, string[]]>(([key, value]) => [key, value.filter(p => typeof p === "string" || p.type === "error").map(p => typeof p === "string" ? p : p.message)])
                .filter(([_key, value]) => value.length > 0)) as Dictionary<string[]>;
            const isClientDataSame = isEqual(
                config.getDataForSave?.(get().clientData) ?? getDataForSave(get().clientData, get().saveProps),
                config.getDataForSave?.(get().serverData) ?? getDataForSave(get().serverData, get().saveProps));
            const isSaveDisabled = (get().entityId !== null && isClientDataSame) ||
                get().isFetching || get().isSaving || get().notFound ||
                Object.keys(propValidationErrors).length > 0;
            set({
                isSaveDisabled, isClientDataSame,
                propValidationMessages: propValidationMessages as Partial<Record<keyof T, ValidationMessage[]>>,
                propValidationErrors: propValidationErrors as Partial<Record<keyof T, string[]>>,
            });
            propValidationMessages$.next(propValidationMessages as Partial<Record<keyof T, ValidationMessage[]>>);
            propValidationErrors$.next(propValidationErrors as Partial<Record<keyof T, string[]>>);
            if (serverDataChanged) {
                serverData$.next({ data: get().serverData, isFetching: get().isFetching });
            }
            clientData$.next({ data: get().clientData, isFetching: get().isFetching });
            if (serverDataChanged && config.onServerDataChanged) {
                config.onServerDataChanged(get().serverData);
            }
        },
        setPropValidators(propValidators) {
            set({ propValidators: propValidators.filter(p => p) as PropValidator<T>[] });
            get().onDataChanged(false);
        },
        reset() {
            set({
                isInitialized: false, failedToFetch: false, notFound: false,
                isFetching: fetchOnInit, isFetched: !fetchOnInit, isSaveDisabled: config.id != null || !fetchOnInit
            });
        },
        enableValidation() {
            if (!get().isValidationEnabled) {
                set({ isValidationEnabled: true });
                isValidationEnabled$.next(true);
                get().onDataChanged(false);
            }
        },
        disableValidation() {
            if (get().isValidationEnabled) {
                set({ isValidationEnabled: false });
                isValidationEnabled$.next(false);
                get().onDataChanged(false);
            }
        },
        setSaveProps(saveProps: (keyof T)[] | undefined) {
            set({ saveProps });
        },
        addDynamicPropValidator(validator) {
            const lastDynamicPropValidatorId = get().lastDynamicPropValidatorId + 1;
            set(p => ({
                dynamicPropValidators: { ...p.dynamicPropValidators, [lastDynamicPropValidatorId]: validator },
                lastDynamicPropValidatorId,
            }));
            get().onDataChanged(false);
            return lastDynamicPropValidatorId;
        },
        removeDynamicPropValidator(id) {
            set(p => {
                const dynamicPropValidators = { ...p.dynamicPropValidators };
                delete dynamicPropValidators[id];
                return { dynamicPropValidators };
            });
            get().onDataChanged(false);
        },
    }));
    return Object.assign(<U = Omit<EntityStore<T>, "init">>(selector: (store: EntityStore<T>) => U = p => p as U, compare: (a: U, b: U) => boolean = shallow) => {
        initStore(useStore);
        return useStore(selector, compare);
    }, { getState: useStore.getState });
}

function initStore<T>(useStore: UseEntityStore<T>) {
    useEffect(() => {
        useStore.getState().init();
    }, [useStore]);
}

export interface EntityValidationStore<T> {
    isClientValid: boolean;
    clientValidators: (false | PropValidator<T> | null | undefined)[];
    setIsClientValid(isClientValid: boolean): void;
    setClientValidators(validators: (false | PropValidator<T> | null | undefined)[]): void;
    revalidate(): void;
}

export function useEntityValidation<T>(useEntityStore: UseEntityStore<T>) {
    const [useIsClientValidStore] = useState(() => zustand<EntityValidationStore<T>>((set, get) => ({
        isClientValid: false,
        clientValidators: [] as (false | PropValidator<T> | null | undefined)[],
        setIsClientValid: isClientValid => set({ isClientValid }),
        setClientValidators: clientValidators => {
            set({ clientValidators });
            get().revalidate();
        },
        revalidate: () => {
            const entity = useEntityStore.getState().clientData;
            const errors = get().clientValidators.map(p => !p ? [] : p(entity)?.messages ?? []).flat(1)
                .filter(p => typeof p === "object" ? p.type === "error" : true);
            get().setIsClientValid(errors.length === 0);
        },
    })));
    useRxJsSub(() => useEntityStore.getState().clientData$.subscribe(() => useIsClientValidStore.getState().revalidate()));
    return useIsClientValidStore;
}

export function useSharedValidation<T, V extends KeysOfTypeSet<T, boolean | null | undefined>>(useMainStore: UseEntityStore<T>, errorProp: V) {
    const substores = useRef<UseEntityStore<any>[]>([]);
    const substoreErrors = useRef<Record<string, boolean>>({});
    useRxJsSub(() => useMainStore.getState().isValidationEnabled$.subscribe(isEnabled => {
        if (isEnabled) {
            substores.current.forEach(p => p.getState().enableValidation());
        }
    }));
    const updateErrorProp = useCallback(() => {
        useMainStore.getState().setClientDataProp(errorProp, Object.values(substoreErrors.current).some(p => p) as T[V]);
    }, [errorProp, useMainStore]);
    const registerStore = useCallback((key: string, useStore: UseEntityStore<any>) => {
        substores.current = [...substores.current, useStore];
        const sub = useStore.getState().propValidationErrors$.pipe(map(p => Object.keys(p).length > 0), distinctUntilChanged()).subscribe(hasErrors => {
            substoreErrors.current[key] = hasErrors;
            updateErrorProp();
        });
        if (useMainStore.getState().isValidationEnabled) {
            useStore.getState().enableValidation();
        }
        return () => {
            sub.unsubscribe();
            substores.current.splice(substores.current.indexOf(useStore), 1);
            delete substoreErrors.current[key];
            updateErrorProp();
        }
    }, [updateErrorProp, useMainStore]);
    return { registerStore };
}
